// File: /opt/stacks/workout/app/src/hooks/useSound.js
//
// Profile-driven audio cue synthesiser using Web Audio API.
// Reads audio settings (volume, mute, profile) from localStorage at call time
// to avoid any React re-render overhead.
// File: /opt/stacks/workout/app/src/hooks/useSound.js
import { useCallback } from "react";
import {
  SOUND_PROFILES,
  DEFAULT_PROFILE,
  HAPTIC_PATTERNS,
  resolveSoundProfileKey,
} from "../config/soundProfiles";
import { getAudioSettings } from "./useAudioSettings";
import { scheduleCue } from "../audio/scheduleCue";
import { appCuesSilenced } from "../audio/audioOutput";
import { nativeCuesOwnAudio } from "../platform/nativeCoach";

// Lazy-initialised Tone.js components shared across the app session.
let Tone = null;
const voices = new Map(); // "set:voice" -> its synth or output, built on first use
let masterGain = null;
let isInitialized = false;
let initializationPromise = null;

// Defensive dedup: ignore a repeat of the SAME sound type fired within this
// window. Guards against React re-running state updaters / effects and firing
// a cue twice a few ms apart (audible as a flanged double). Legitimate repeats
// are always far wider apart than this (countdown_tick is ≥1s by interval
// design; phase cues ≥1s; pause/resume/UI previews are discrete user taps).
const SOUND_DEDUP_MS = 70;
const lastPlayedAt = new Map(); // type -> high-res timestamp (ms)

async function ensureAudioContext() {
  if (!isInitialized) {
    if (!initializationPromise) {
      initializationPromise = (async () => {
        // Dynamic import prevents Tone.js from creating an AudioContext at module load.
        Tone = await import("tone");
        // Tone.start() is required by modern browsers to unlock audio after a user gesture.
        await Tone.start();

        // The limiter catches overlapping cues (a bell still ringing out under
        // the next one) that would otherwise clip.
        masterGain = new Tone.Gain(1).connect(new Tone.Limiter(-1).toDestination());

        isInitialized = true;
      })().catch((error) => {
        initializationPromise = null;
        throw error;
      });
    }

    await initializationPromise;
  }

  // Mobile browsers suspend the AudioContext on backgrounding / audio-focus
  // loss AFTER initialisation, and the isInitialized latch used to make this
  // call a no-op then. While suspended, Tone.now() is frozen — anything
  // scheduled piles onto one timestamp and bursts out garbled when the
  // context resumes. Always try to bring the context back here.
  if (Tone.getContext().state !== "running") {
    try {
      await Tone.start();
    } catch {
      // Outside a user gesture the browser may refuse; callers must check
      // the context state before scheduling.
    }
  }
}

// `preview` is for the Settings cue list: it plays a tick even with countdown
// ticks off, never vibrates, and can name the sound set to use (a set picked a
// moment ago may not have reached storage yet).
const useSound = () => {
  const playSound = useCallback(async (type, { preview = false, profile: profileOverride } = {}) => {
    try {
      const settings = getAudioSettings();

      // Early exits
      if (settings.muted) return;
      if (!preview && appCuesSilenced(settings.output)) return;
      // Android app in the background: CoachService is playing the cues.
      if (!preview && nativeCuesOwnAudio()) return;
      if (type === "countdown_tick" && !settings.countdownEnabled && !preview) return;

      // Defensive dedup against duplicate React-driven calls for one event.
      // Runs synchronously before any await, so two calls in the same tick
      // both reach here and the second is dropped.
      const nowMs =
        typeof performance !== "undefined" && performance.now
          ? performance.now()
          : Date.now();
      const prevMs = lastPlayedAt.get(type);
      if (prevMs !== undefined && nowMs - prevMs < SOUND_DEDUP_MS) return;
      lastPlayedAt.set(type, nowMs);

      await ensureAudioContext();

      // If the context still isn't running (a resume attempted outside a user
      // gesture can be refused), drop the cue rather than schedule it into a
      // frozen clock — queued notes all fire at once, overlapped and garbled,
      // whenever the context finally resumes.
      if (Tone.getContext().state !== "running") return;

      // Set master volume (React slider is 0.0 - 1.0)
      masterGain.gain.rampTo(settings.volume, 0.05);

      // Resolve profile and sound config
      const requestedKey = resolveSoundProfileKey(profileOverride || settings.profile);
      const profileKey = SOUND_PROFILES[requestedKey] ? requestedKey : DEFAULT_PROFILE;
      const profile = SOUND_PROFILES[profileKey];
      if (!Array.isArray(profile.sounds?.[type]) || profile.sounds[type].length === 0) {
        return;
      }

      scheduleCue({
        Tone,
        setKey: profileKey,
        profile,
        type,
        at: Tone.now(),
        destination: masterGain,
        cache: voices,
      });

      // Haptic feedback (progressive enhancement)
      if (settings.hapticEnabled && !preview && navigator.vibrate) {
        const pattern = HAPTIC_PATTERNS[type];
        if (pattern) navigator.vibrate(pattern);
      }
    } catch (error) {
      console.error("Could not play sound:", error);
    }
  }, []);

  return playSound;
};

// Whether a user gesture has already unlocked the shared AudioContext. Lets the
// live follower decide if a late joiner still needs a "tap to enable sound"
// prompt (browsers won't play audio until Tone.start() runs inside a gesture).
function isAudioUnlocked() {
  return isInitialized;
}

export { ensureAudioContext, isAudioUnlocked };
export default useSound;
